import { createProductIcon } from './product-icon.js';
import { createPositiveLogo } from './positive-logo.js';
import { createProductActions } from './product-actions.js';
import { lockScroll } from '../core/scroll-lock.js';
import { createInterfaceIcon } from './interface-icon.js';
import { isControlActivation } from '../core/navigation.js';

/** One native dialog per instance, shared by catalog and product details. */
export function createProductPanels(host, { onAction, onLifecycle, onModalChange, isMobile, restoreInvoker }) {
  const document = host.ownerDocument;
  const root = host.shadowRoot;
  const node = (tag, name) => {
    const element = document.createElement(tag);
    element.className = `positive-nav-panel${name ? `-${name}`: ''}`;
    return element;
  };
  const symbol = (name) => {
    const svg = createInterfaceIcon(name === 'close' ? 'times': name, document);
    svg.classList.add('positive-nav-panel-symbol');
    return svg;
  };
  const button = (name, action) => {
    const element = node('button', name);
    element.type = 'button';
    element.dataset.action = action;
    return element;
  };
  const element = node('dialog');
  element.id = `${host.instanceId}-panel`;
  element.setAttribute('aria-modal', 'true');
  const handle = node('div', 'handle');
  handle.setAttribute('aria-hidden', 'true');
  const header = node('header', 'header');
  const back = button('back', 'openCatalog');
  back.hidden = true;
  const backIcon = symbol('angle-right');
  const backLabel = node('span', 'back-label');
  back.append(backIcon, backLabel);
  const toolbar = node('div', 'toolbar');
  const dismiss = button('close', 'close');
  dismiss.append(symbol('close'));
  const hero = node('div', 'hero');
  const heroIcon = node('span', 'icon');
  const heading = node('div', 'heading');
  const category = node('span', 'category');
  const title = node('h2', 'title');
  const catalogLogo = createPositiveLogo(document);
  title.id = `${element.id}-title`;
  title.tabIndex = -1;
  element.setAttribute('aria-labelledby', title.id);
  const subtitle = node('p', 'subtitle');
  const description = node('p', 'description');
  heading.append(title, category, subtitle);
  hero.append(heroIcon, heading);
  const current = node('div', 'current');
  toolbar.append(current, dismiss);
  header.append(back, hero, toolbar);
  const body = node('div', 'body');
  const catalogView = node('section', 'catalog');
  const grid = node('div', 'grid');
  const empty = node('p', 'empty');
  const mobileCatalog = node('div', 'mobile-catalog');
  mobileCatalog.hidden = true;
  const currentTitle = node('h3', 'list-title');
  const currentRow = node('div', 'list-current');
  currentRow.setAttribute('aria-current', 'page');
  const currentIcon = node('span', 'list-icon');
  const currentHeading = node('div', 'list-heading');
  const currentName = node('span', 'list-name');
  const currentTagline = node('span', 'list-tagline');
  currentHeading.append(currentName, currentTagline);
  currentRow.append(currentIcon, currentHeading, symbol('check'));
  const listTitle = node('h3', 'list-title');
  listTitle.id = `${element.id}-list-title`;
  const list = node('ul', 'list');
  list.setAttribute('aria-labelledby', listTitle.id);
  mobileCatalog.append(currentTitle, currentRow, listTitle, list);
  catalogView.append(grid, mobileCatalog, empty);
  const detailView = node('section', 'detail');
  const featuresBox = node('div', 'features-box');
  const featuresTitle = node('h3', 'features-title');
  const features = node('ul', 'features');
  featuresBox.append(featuresTitle, features);
  const actions = createProductActions(document, 'positive-nav-panel', { source: 'productPanel' });
  detailView.append(description, featuresBox, actions.element);
  body.append(catalogView, detailView);
  element.append(handle, header, body);
  root.append(element);

  let catalog;
  let t;
  let view;
  let invoker;
  let releaseScroll;
  let revision = 0;
  let disposed = false;
  let backdropDown = false;
  let heroId;
  let currentId;
  let listCurrentId;
  let catalogScroll = 0;
  let catalogProductId;
  const cards = new Map();
  const rows = new Map();
  const payload = (state) => ({ panel: state.panel, ...(state.productId ? { productId: state.productId }: {}) });
  const getProduct = (id) => catalog?.visibleProducts.find((product) => product.id === id && !product.isCurrent);
  const focusTitle = () => title.focus({ preventScroll: true });
  const invokerFrom = (event) => event?.composedPath?.().find((item) => item?.localName === 'button' || item?.localName === 'a');

  function renderFeatures(list, values) {
    while (list.children.length > values.length) list.lastElementChild.remove();
    values.forEach((value, index) => {
      let item = list.children[index];
      if (!item) {
        item = node('li', 'feature');
        const icon = node('span', 'feature-icon');
        icon.append(symbol('check'));
        const label = node('span', 'feature-label');
        item.append(icon, label);
        list.append(item);
      }
      item.lastElementChild.textContent = value;
    });
  }

  function createCard(product) {
    const element = node('article', 'card');
    element.dataset.productId = product.id;
    const header = node('div', 'card-header');
    const icon = node('span', 'icon');
    icon.append(createProductIcon(product.logoId, document));
    const heading = node('div', 'card-heading');
    const nameRow = node('div', 'card-name-row');
    const name = node('h3', 'card-name');
    const status = node('span', 'card-status');
    status.classList.add('positive-nav-panel-status');
    nameRow.append(name, status);
    const category = node('span', 'card-category');
    heading.append(nameRow, category);
    header.append(icon, heading);
    const description = node('p', 'card-description');
    const features = node('ul', 'features');
    const actions = createProductActions(document, 'positive-nav-panel', { source: 'catalog' });
    element.append(header, description, features, actions.element);
    return { element, name, status, category, description, features, actions };
  }

  function renderList(products) {
    currentTitle.textContent = t('status.current');
    currentTitle.hidden = !catalog.currentProduct;
    currentRow.hidden = !catalog.currentProduct;
    if (catalog.currentProduct) {
      const product = catalog.currentProduct;
      if (listCurrentId !== product.id) {
        currentIcon.replaceChildren(createProductIcon(product.logoId, document));
        listCurrentId = product.id;
      }
      currentName.textContent = product.name;
      currentTagline.textContent = product.tagline;
    }
    listTitle.textContent = t('nav.discover');
    listTitle.hidden = products.length === 0;
    const visible = new Set(products.map(({ id }) => id));
    for (const [id, row] of rows) if (!visible.has(id)) { row.element.remove(); rows.delete(id); }
    products.forEach((product, index) => {
      let row = rows.get(product.id);
      if (!row) {
        const item = node('li', 'list-item');
        const trigger = button('list-product', 'openProduct');
        trigger.dataset.productId = product.id;
        const icon = node('span', 'list-icon');
        icon.append(createProductIcon(product.logoId, document));
        const heading = node('span', 'list-heading');
        const nameRow = node('span', 'list-name-row');
        const name = node('span', 'list-name');
        const status = node('span', 'list-status');
        status.classList.add('positive-nav-panel-status');
        nameRow.append(name, status);
        const tagline = node('span', 'list-tagline');
        heading.append(nameRow, tagline);
        const chevron = symbol('angle-right');
        chevron.classList.add('positive-nav-panel-list-chevron');
        trigger.append(icon, heading, chevron);
        trigger.setAttribute('aria-controls', element.id);
        trigger.setAttribute('aria-haspopup', 'dialog');
        item.append(trigger);
        row = { element: item, trigger, name, status, tagline };
        rows.set(product.id, row);
      }
      row.name.textContent = product.name;
      row.status.textContent = t('status.connected');
      row.status.hidden = !product.isConnected;
      row.tagline.textContent = product.tagline;
      row.trigger.dataset.status = product.status;
      const label = t('nav.discoverProduct', { product: product.name });
      row.trigger.setAttribute('aria-label', product.isConnected ? `${label} · ${t('status.connected')}`: label);
      if (list.children[index] !== row.element) list.insertBefore(row.element, list.children[index] ?? null);
    });
  }

  function render(focused = root.activeElement) {
    dismiss.setAttribute('aria-label', t('actions.close'));
    dismiss.title = t('actions.close');
    const isCatalog = view.panel === 'catalog';
    const mobile = isMobile();
    element.dataset.view = view.panel;
    back.hidden = isCatalog || !mobile;
    backLabel.textContent = t('nav.allProducts');
    back.setAttribute('aria-label', t('actions.backToCatalog'));
    heroIcon.hidden = isCatalog;
    category.hidden = isCatalog;
    subtitle.hidden = !isCatalog || mobile;
    description.hidden = isCatalog;
    current.hidden = !isCatalog || !catalog.currentProduct || mobile;
    catalogView.hidden = !isCatalog;
    detailView.hidden = isCatalog;
    if (isCatalog && mobile) title.setAttribute('aria-label', t('nav.allProducts'));
    else title.removeAttribute('aria-label');
    if (isCatalog) {
      title.replaceChildren(mobile ? catalogLogo: t('catalog.title'));
      subtitle.textContent = catalog.currentProduct
        ? t('catalog.description', { product: catalog.currentProduct.name }): t('catalog.descriptionDefault');
      if (catalog.currentProduct) {
        if (currentId !== catalog.currentProduct.id) {
          current.replaceChildren(createProductIcon(catalog.currentProduct.logoId, document), node('span', 'current-label'));
          currentId = catalog.currentProduct.id;
        }
        const [prefix, suffix = ''] = t('nav.currentProduct').split('{product}');
        const productName = node('span', 'current-name');
        productName.textContent = catalog.currentProduct.name;
        current.lastElementChild.replaceChildren(prefix, productName, suffix);
      }
      const products = catalog.visibleProducts.filter((product) => !product.isCurrent);
      renderList(products);
      const visible = new Set(products.map((product) => product.id));
      for (const [id, card] of cards) if (!visible.has(id)) { card.element.remove(); cards.delete(id); }
      products.forEach((product, index) => {
        let card = cards.get(product.id);
        if (!card) { card = createCard(product); cards.set(product.id, card); }
        card.name.textContent = product.name;
        card.status.textContent = t('status.connected');
        card.status.hidden = !product.isConnected;
        card.category.textContent = product.category;
        card.description.textContent = product.description;
        card.element.dataset.status = product.status;
        renderFeatures(card.features, product.features);
        card.actions.update(product, t, catalog.currentProduct?.id);
        if (grid.children[index] !== card.element) grid.insertBefore(card.element, grid.children[index] ?? null);
      });
      empty.textContent = t('catalog.empty');
      empty.hidden = products.length > 0;
      grid.hidden = products.length === 0 || mobile;
      mobileCatalog.hidden = !mobile;
    } else {
      const product = getProduct(view.productId);
      if (heroId !== product.id) { heroIcon.replaceChildren(createProductIcon(product.logoId, document)); heroId = product.id; }
      title.textContent = product.name;
      category.textContent = product.category;
      description.textContent = product.description;
      featuresTitle.textContent = t('product.featuresTitle', { product: product.name });
      renderFeatures(features, product.features);
      actions.update(product, t, catalog.currentProduct?.id);
    }
    // Changing access may move focus from Discover to Sign in.
    // Preserve this intentional focus transfer.
    const active = root.activeElement;
    if (active && element.contains(active) && !active.closest('[hidden]') && active.getClientRects().length) return;
    if (focused && element.open && (element.contains(focused) || !focused.isConnected)) focusTitle();
  }

  function open(action, originalEvent) {
    if (disposed || !host.isConnected || !catalog) return;
    const panel = action.action === 'openCatalog' ? 'catalog': 'product';
    if (panel === 'product' && !getProduct(action.productId)) return;
    if (view?.panel === panel && view.productId === action.productId) return;
    const previous = view;
    const next = { panel, productId: panel === 'product' ? action.productId: undefined, announced: false };
    const version = ++revision;
    const trigger = invokerFrom(originalEvent);
    if (trigger && !element.contains(trigger)) invoker = trigger;
    if (!previous) { catalogScroll = 0; catalogProductId = undefined; }
    if (previous?.panel === 'catalog') {
      catalogScroll = body.scrollTop;
      catalogProductId = panel === 'product' ? action.productId: undefined;
    }
    view = next;
    onModalChange(true);
    try {
      render();
      if (!element.open) {
        element.showModal();
        releaseScroll = lockScroll(document);
      }
    } catch (error) {
      view = undefined;
      onModalChange(false);
      throw error;
    }
    const returning = panel === 'catalog' && action.source === 'back';
    body.scrollTop = returning ? catalogScroll: 0;
    const row = returning && isMobile() ? rows.get(catalogProductId)?.trigger: null;
    if (row?.getClientRects().length) row.focus({ preventScroll: true });
    else focusTitle();
    if (previous?.announced) onLifecycle('onClose', { ...payload(previous), reason: action.reason ?? 'switch' });
    if (!disposed && revision === version) {
      next.announced = true;
      onLifecycle('onOpen', { ...payload(next), source: action.source });
    }
  }

  function close({ reason = 'button', restoreFocus = true, panel, productId } = {}) {
    if (!view) return;
    if (panel && (panel !== view.panel || productId !== view.productId)) return;
    const previous = view;
    const trigger = invoker;
    view = undefined;
    invoker = undefined;
    revision++;
    if (element.open) element.close();
    releaseScroll?.();
    releaseScroll = undefined;
    delete element.dataset.view;
    if (restoreFocus && host.isConnected) restoreInvoker(trigger);
    onModalChange(false);
    if (previous.announced) onLifecycle('onClose', { ...payload(previous), reason });
  }

  function requestClose(reason, event) {
    if (!view || event.defaultPrevented) return;
    onAction({ action: 'close', source: 'panel', ...payload(view), reason }, event);
  }
  const outside = (event) => {
    const rect = element.getBoundingClientRect();
    return event.target === element && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
  };
  function pointerDown(event) { backdropDown = outside(event); }
  function click(event) {
    if (!view || event.defaultPrevented) return;
    if (event.type === 'click' && event.button === 0 && backdropDown && outside(event)) { backdropDown = false; requestClose('outside', event); return; }
    backdropDown = false;
    const trigger = event.target.closest?.('[data-action]');
    if (!trigger || !element.contains(trigger) || !isControlActivation(event, trigger)) return;
    const action = trigger.dataset.action;
    if (action === 'close') { requestClose('button', event); return; }
    if (action === 'openCatalog' && trigger === back) { onAction({ action, source: 'back' }, event); return; }
    if (action === 'openProduct' && isMobile() && view.panel === 'catalog') {
      const product = getProduct(trigger.dataset.productId);
      if (product) onAction({ action, source: 'catalog', productId: product.id }, event);
      return;
    }
    if (action !== 'navigate') return;
    const card = trigger.closest('.positive-nav-panel-card');
    const product = getProduct(card?.dataset.productId ?? view.productId);
    if (!product) return;
    const source = card ? 'catalog': 'productPanel';
    onAction({ action: 'navigate', source, productId: product.id, destination: trigger.dataset.destination,
      url: product.urls[trigger.dataset.destination], target: '_blank' }, event);
  }
  function cancel(event) {
    if (!view || event.defaultPrevented) return;
    onAction({ action: 'close', source: 'panel', ...payload(view), reason: 'escape' }, event);
    event.preventDefault();
  }
  function keydown(event) {
    if (!view || event.defaultPrevented) return;
    if (event.key === 'Escape') { requestClose('escape', event); event.preventDefault(); return; }
    if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
    const controls = [...element.querySelectorAll('button:not(:disabled), a[href]')]
      .filter((control) => !control.closest('[hidden]') && control.getClientRects().length);
    const first = controls[0];
    const last = controls[controls.length - 1];
    const focused = root.activeElement;
    if (focused === title || (event.shiftKey ? focused === first: focused === last)) {
      event.preventDefault();
      (event.shiftKey ? last: first)?.focus();
    }
  }
  function nativeClose() { if (!element.open) close({ reason: 'native' }); }
  element.addEventListener('pointerdown', pointerDown);
  element.addEventListener('click', click);
  element.addEventListener('auxclick', click);
  element.addEventListener('cancel', cancel);
  element.addEventListener('keydown', keydown);
  element.addEventListener('close', nativeClose);
  return {
    element, open, close,
    refreshLayout(focused) { if (view && !disposed) render(focused); },
    update(nextCatalog, translate) {
      catalog = nextCatalog;
      t = translate;
      if (!view || disposed) return;
      if (view.panel === 'product' && !getProduct(view.productId)) close({ reason: 'hidden' });
      else render();
    },
    dispose() {
      disposed = true;
      close({ reason: 'destroy', restoreFocus: false });
      element.removeEventListener('pointerdown', pointerDown);
      element.removeEventListener('click', click);
      element.removeEventListener('auxclick', click);
      element.removeEventListener('cancel', cancel);
      element.removeEventListener('keydown', keydown);
      element.removeEventListener('close', nativeClose);
      element.remove();
      cards.clear();
      rows.clear();
      catalog = undefined;
      t = undefined;
    },
  };
}
